import { Alert, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './onboarding.module.css'
import { AppLayout } from '@/components/AppLayout'
import { api, type Sequence, type StripeStatus } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/onboarding')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: OnboardingPage,
})

const STEP_LABELS = ['Connect Stripe', 'Review Sequence', 'Activate']

function OnboardingPage() {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const [stripeStatus, setStripeStatus] = useState<StripeStatus | null>(null)
  const [stripeLoading, setStripeLoading] = useState(true)
  const [stripeConnecting, setStripeConnecting] = useState(false)
  const [sequences, setSequences] = useState<Sequence[]>([])
  const [sequencesLoading, setSequencesLoading] = useState(false)
  const [activating, setActivating] = useState(false)
  const [activated, setActivated] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Step 1: Check Stripe status on mount
  useEffect(() => {
    api
      .getStripeStatus()
      .then((response) => {
        setStripeStatus(response.data)
        if (response.data.connected) {
          setCurrentStep(1)
        }
      })
      .catch(() => {
        // Stripe not connected, stay on step 0
      })
      .finally(() => {
        setStripeLoading(false)
      })
  }, [])

  // Step 2: Fetch sequences when reaching step 1
  useEffect(() => {
    if (currentStep !== 1) return
    let cancelled = false
    api
      .listSequences()
      .then((response) => {
        if (!cancelled) {
          setError(null)
          setSequences(response.data)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load sequences')
        }
      })
      .finally(() => {
        if (!cancelled) setSequencesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentStep])

  const handleConnectStripe = () => {
    setStripeConnecting(true)
    setError(null)
    api
      .getStripeConnectUrl()
      .then((response) => {
        window.location.href = response.data.redirect_url
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to get Stripe connect URL')
        setStripeConnecting(false)
      })
  }

  const handleLooksGood = () => {
    setCurrentStep(2)
  }

  const defaultSequence =
    sequences.find((s) => s.isDefault) ?? (sequences.length > 0 ? sequences[0] : null)

  const handleActivate = () => {
    if (!defaultSequence) return
    setActivating(true)
    setError(null)
    api
      .activateSequence(defaultSequence.id)
      .then(() => {
        setActivated(true)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to activate sequence')
      })
      .finally(() => {
        setActivating(false)
      })
  }

  const handleGoToDashboard = () => {
    void navigate({ to: '/dashboard' })
  }

  return (
    <AppLayout title="Getting Started">
      <div className={styles.onboarding}>
        {/* Step Indicator */}
        <div className={styles.stepIndicator}>
          {STEP_LABELS.map((label, index) => {
            const isCompleted = index < currentStep
            const isActive = index === currentStep
            const stepGroupClass = isCompleted
              ? styles.stepGroupCompleted
              : isActive
                ? styles.stepGroupActive
                : ''

            return (
              <div key={label} style={{ display: 'flex', alignItems: 'center' }}>
                {index > 0 && (
                  <div
                    className={`${styles.stepLine} ${isCompleted ? (styles.stepLineCompleted ?? '') : ''}`}
                  />
                )}
                <div className={`${styles.stepGroup} ${stepGroupClass ?? ''}`}>
                  <div
                    className={`${styles.stepCircle} ${
                      isCompleted
                        ? (styles.stepCircleCompleted ?? '')
                        : isActive
                          ? (styles.stepCircleActive ?? '')
                          : ''
                    }`}
                  >
                    {isCompleted ? '\u2713' : index + 1}
                  </div>
                  <span className={styles.stepLabel}>{label}</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Step Content */}
        <div className={styles.stepContent}>
          {error && (
            <Alert variant="danger" style={{ marginBottom: '1rem' }}>
              {error}
            </Alert>
          )}

          {/* Step 1: Connect Stripe */}
          {currentStep === 0 && (
            <Card className={styles.stripeCard}>
              <CardBody>
                {stripeLoading ? (
                  <div className={styles.loadingState}>
                    <Spinner />
                    <span>Checking Stripe connection...</span>
                  </div>
                ) : stripeStatus?.connected ? (
                  <div className={styles.stripeConnected}>
                    <span className={styles.stripeConnectedIcon}>{'\u2705'}</span>
                    <span className={styles.stripeConnectedText}>Stripe Connected</span>
                    <span className={styles.stripeConnectedSub}>
                      Account {stripeStatus.stripe_account_id}
                    </span>
                    <div className={styles.stepActions}>
                      <Button variant="primary" onClick={() => setCurrentStep(1)}>
                        Continue
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.stripeIcon}>{'\uD83D\uDCB3'}</div>
                    <h3 className={styles.stripeTitle}>Connect Your Stripe Account</h3>
                    <p className={styles.stripeDescription}>
                      BounceBack needs access to your Stripe account to detect subscription
                      cancellations and trigger automated win-back email sequences.
                    </p>
                    <Button
                      variant="primary"
                      onClick={handleConnectStripe}
                      loading={stripeConnecting}
                    >
                      Connect Stripe
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>
          )}

          {/* Step 2: Review Default Sequence */}
          {currentStep === 1 && (
            <Card>
              <CardBody>
                {sequencesLoading ? (
                  <div className={styles.loadingState}>
                    <Spinner />
                    <span>Loading sequences...</span>
                  </div>
                ) : defaultSequence ? (
                  <div className={styles.sequencePreview}>
                    <h3 className={styles.sequencePreviewTitle}>{defaultSequence.name}</h3>
                    {defaultSequence.description && (
                      <p className={styles.sequencePreviewDescription}>
                        {defaultSequence.description}
                      </p>
                    )}
                    {defaultSequence.steps.length > 0 ? (
                      defaultSequence.steps
                        .sort((a, b) => a.stepNumber - b.stepNumber)
                        .map((step) => (
                          <div key={step.id} className={styles.sequenceStep}>
                            <span className={styles.stepDelay}>
                              {step.delayDays === 0
                                ? 'Immediate'
                                : step.delayDays === 1
                                  ? '1 day'
                                  : `${step.delayDays} days`}
                            </span>
                            <div className={styles.stepDetails}>
                              <div className={styles.stepSubject}>{step.subjectTemplate}</div>
                              <div className={styles.stepBody}>{step.bodyTemplate}</div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <p className={styles.noSequences}>
                        This sequence has no steps yet. You can add steps in the sequence editor.
                      </p>
                    )}
                    <div className={styles.stepActions}>
                      <Link to="/sequences">
                        <Button variant="secondary">Edit in Sequences</Button>
                      </Link>
                      <Button variant="primary" onClick={handleLooksGood}>
                        Looks Good
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.noSequences}>
                    <p>No sequences found. Create a sequence to get started.</p>
                    <div className={styles.stepActions}>
                      <Link to="/sequences">
                        <Button variant="primary">Create Sequence</Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Step 3: Activate */}
          {currentStep === 2 && (
            <Card>
              <CardBody>
                {activated ? (
                  <div className={styles.successState}>
                    <div className={styles.successIcon}>{'\u2713'}</div>
                    <h3 className={styles.successTitle}>You&apos;re All Set!</h3>
                    <p className={styles.successDescription}>
                      Your win-back sequence is now active. BounceBack will automatically send
                      recovery emails when subscribers cancel their subscriptions.
                    </p>
                    <Button variant="primary" onClick={handleGoToDashboard}>
                      Go to Dashboard
                    </Button>
                  </div>
                ) : (
                  <div className={styles.successState}>
                    <div className={styles.stripeIcon}>{'\uD83D\uDE80'}</div>
                    <h3 className={styles.successTitle}>Ready to Activate</h3>
                    <p className={styles.successDescription}>
                      Activate your default win-back sequence to start recovering churned
                      subscribers automatically. You can adjust settings anytime from the Sequences
                      page.
                    </p>
                    {defaultSequence && (
                      <Button variant="primary" onClick={handleActivate} loading={activating}>
                        Activate Sequence
                      </Button>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
